import { useState } from "react";
import { HelpCircle, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DEFAULT_NEGOTIATION_CONFIG,
  type NegotiationSignalConfig,
  type SignalKey,
} from "@/lib/par/negotiationSuggestions";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  config: NegotiationSignalConfig;
  onSave: (patch: Partial<NegotiationSignalConfig>) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Signal metadata for rendering
// ---------------------------------------------------------------------------

interface ThresholdConfig {
  key: keyof NegotiationSignalConfig;
  label: string;
  min: number;
  max?: number;
  step: number;
  info: string;
}

interface SignalRowConfig {
  key: SignalKey;
  label: string;
  info: string;
  threshold?: ThresholdConfig;
}

const SIGNAL_ROWS: SignalRowConfig[] = [
  {
    key: "mixed_terms",
    label: "Mixed payment terms",
    info: "Vendor has used more than one distinct payment-terms string.",
  },
  {
    key: "paid_late",
    label: "Paid late or sporadic",
    info: "Vendor pays late on average, or payment timing is erratic. Threshold is the std-dev of days-paid-early (in days) above which a vendor counts as sporadic.",
    threshold: {
      key: "late_variance_stddev_days",
      label: "Late variance (std-dev days)",
      min: 0,
      step: 1,
      info: "Standard deviation of days-paid-early above which a vendor is considered sporadic.",
    },
  },
  {
    key: "high_spend",
    label: "High spend",
    info: "Vendor is among your highest-spend suppliers. Threshold is the spend quantile cutoff (e.g. 0.75 = top quartile).",
    threshold: {
      key: "high_spend_quantile",
      label: "High-spend quantile",
      min: 0,
      max: 1,
      step: 0.05,
      info: "Spend quantile cutoff — vendors above this percentile are considered high-spend (e.g. 0.75 = top quartile).",
    },
  },
  {
    key: "no_discount",
    label: "No early-pay discount",
    info: "Vendor currently offers no early-payment discount.",
  },
  {
    key: "paid_early_no_capture",
    label: "Paid early, no capture",
    info: "You already pay this vendor early but capture little/no discount. Threshold is the near-zero capture amount in dollars.",
    threshold: {
      key: "near_zero_capture",
      label: "Near-zero capture ($)",
      min: 0,
      step: 0.01,
      info: "Dollar amount below which captured discount is considered near-zero.",
    },
  },
  {
    key: "long_terms",
    label: "Long net terms",
    info: "Vendor's net payment terms are long. Threshold is the minimum net days (e.g. 45).",
    threshold: {
      key: "long_terms_min_days",
      label: "Long-terms min days",
      min: 0,
      step: 1,
      info: "Minimum net days above which a vendor's terms are considered long (e.g. 45).",
    },
  },
];

// ---------------------------------------------------------------------------
// Small helper: info tooltip bubble
// ---------------------------------------------------------------------------

function InfoTip({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help flex-shrink-0" />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] text-xs">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SignalConfigDialog({ config, onSave }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<NegotiationSignalConfig>(config);
  const [saving, setSaving] = useState(false);

  function resetDraft() {
    setDraft(config);
  }

  function handleOpenChange(next: boolean) {
    if (next) resetDraft();
    setOpen(next);
  }

  function setThreshold<K extends keyof NegotiationSignalConfig>(key: K, raw: string) {
    const n = parseFloat(raw);
    if (!isNaN(n)) setDraft((d) => ({ ...d, [key]: n }));
  }

  function setWeight(signalKey: SignalKey, raw: string) {
    const n = parseFloat(raw);
    if (!isNaN(n))
      setDraft((d) => ({ ...d, [`weight_${signalKey}` as keyof NegotiationSignalConfig]: n }));
  }

  function setEnabled(signalKey: SignalKey, value: boolean) {
    setDraft((d) => ({ ...d, [`enabled_${signalKey}` as keyof NegotiationSignalConfig]: value }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(draft);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setDraft(DEFAULT_NEGOTIATION_CONFIG);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Configure signal settings">
          <Settings className="w-3.5 h-3.5 text-muted-foreground" />
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Suggested Vendor Signal Config</DialogTitle>
        </DialogHeader>

        {/* Per-signal toggles, weights, and nested thresholds */}
        <section className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Signals
          </h4>

          <div className="rounded-md border divide-y">
            {SIGNAL_ROWS.map(({ key, label, info, threshold }) => {
              const enabledKey = `enabled_${key}` as keyof NegotiationSignalConfig;
              const weightKey = `weight_${key}` as keyof NegotiationSignalConfig;
              const isEnabled = draft[enabledKey] as boolean;
              const weight = draft[weightKey] as number;

              return (
                <div key={key} className="px-3 py-2.5 space-y-2">
                  {/* Top row: toggle + label + weight */}
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={(v) => setEnabled(key, v)}
                      className="flex-shrink-0"
                    />
                    <span
                      className={`flex-1 text-xs flex items-center gap-1 ${isEnabled ? "text-foreground" : "text-muted-foreground line-through"}`}
                    >
                      {label}
                      <InfoTip text={info} />
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        Weight
                        <InfoTip text="How much this signal counts toward a vendor's overall ranking score." />
                      </span>
                      <Input
                        type="number"
                        min={0}
                        step={0.5}
                        value={weight}
                        onChange={(e) => setWeight(key, e.target.value)}
                        disabled={!isEnabled}
                        className="h-7 w-16 text-xs text-center"
                      />
                    </div>
                  </div>

                  {/* Nested threshold row (only for signals that have one) */}
                  {threshold && (
                    <div className="ml-9 flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1 whitespace-nowrap">
                        {threshold.label}
                        <InfoTip text={threshold.info} />
                      </Label>
                      <Input
                        type="number"
                        min={threshold.min}
                        max={threshold.max}
                        step={threshold.step}
                        value={draft[threshold.key] as number}
                        onChange={(e) => setThreshold(threshold.key, e.target.value)}
                        disabled={!isEnabled}
                        className="h-7 w-24 text-xs text-center"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Actions */}
        <div className="flex justify-between pt-1">
          <Button variant="ghost" size="sm" onClick={handleReset} disabled={saving}>
            Reset to defaults
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
